import { FREE_TIER_MESSAGE_LIMIT } from '@/lib/constants'

// Set these to your Stripe Payment Link URLs when unpaused
export const STRIPE_LINKS = {
  founding: 'https://buy.stripe.com/28E5kF7Mx94Afss45ads405',
  annual: 'https://buy.stripe.com/cNifZjd6R4Ok3JK8lqds404',
  monthly: 'https://buy.stripe.com/dRmbJ30k5gx2bccbxCds403',
  // max: '<paste max payment link here when Max ships>',
}

// Plan tier registry — mirrored from supabase/functions/_shared/plans.ts.
// `subscription_plan` on profiles is one of these strings (or null for
// free users). Use PLAN_TIER constants instead of bare strings so a
// rename catches at the type/import level.
export const PLAN_TIER = {
  PREMIUM_MONTHLY: 'premium_monthly',
  PREMIUM_ANNUAL: 'premium_annual',
  FOUNDING_ANNUAL: 'founding_annual',
  MAX_MONTHLY: 'max_monthly',
}

// Tier rank — premium variants all share rank 1; Max sits at 2. Mirrors
// supabase/functions/_shared/plans.ts:TIER_RANK. When Max launches, gate
// Max-only capabilities with `getTierRank(plan) >= 2`.
const TIER_RANK = {
  [PLAN_TIER.PREMIUM_MONTHLY]: 1,
  [PLAN_TIER.PREMIUM_ANNUAL]: 1,
  [PLAN_TIER.FOUNDING_ANNUAL]: 1,
  [PLAN_TIER.MAX_MONTHLY]: 2,
}

export function getTierRank(plan) {
  if (!plan) return 0
  return TIER_RANK[plan] ?? 0
}

export function isMaxTier(plan) {
  return plan === PLAN_TIER.MAX_MONTHLY
}

export const FOUNDING_SPOTS_TOTAL = 200

// ── Base prices (source of truth, mirrored to Stripe) ────────────
// When these change, update the matching price in Stripe via MCP in
// the same commit. All derived values below recompute automatically,
// so display copy across the app stays consistent.
export const PREMIUM_MONTHLY_PRICE = 12.49
export const PREMIUM_ANNUAL_PRICE = 99.99
export const FOUNDING_ANNUAL_PRICE = 79.99

// ── Derived values — DO NOT hardcode these, import them ─────────
export const MONTHLY_X12 = +(PREMIUM_MONTHLY_PRICE * 12).toFixed(2) // 149.88
export const ANNUAL_SAVINGS_USD = +(MONTHLY_X12 - PREMIUM_ANNUAL_PRICE).toFixed(2) // 49.89
export const FOUNDING_SAVINGS_USD = +(MONTHLY_X12 - FOUNDING_ANNUAL_PRICE).toFixed(2) // 69.89
export const ANNUAL_PCT_OFF = Math.round((1 - PREMIUM_ANNUAL_PRICE / MONTHLY_X12) * 100) // 33
export const FOUNDING_PCT_OFF = Math.round((1 - FOUNDING_ANNUAL_PRICE / MONTHLY_X12) * 100) // 47

// Display helpers — pre-formatted to 2 decimals so "$6.67" / "$8.33" look right everywhere
export const ANNUAL_MONTHLY_DISPLAY = (PREMIUM_ANNUAL_PRICE / 12).toFixed(2) // '8.33'
export const FOUNDING_MONTHLY_DISPLAY = (FOUNDING_ANNUAL_PRICE / 12).toFixed(2) // '6.67'

// BENEFITS — only list what exists today. No vaporware on the pricing page.
// Coming-soon features live in the landing roadmap section.
export const BENEFITS = [
  { icon: '💬', label: 'Four AI companions — Grace, Logos, Spurgeon, and Ezra' },
  { icon: '📖', label: 'Bible Reader with AI Insight in 8 translations (5 PD + NLT, NKJV, NASB)' },
  {
    icon: '🔍',
    label: 'Unlimited Bible AI Insight — historical, theological, original-language, and more',
  },
  { icon: '✨', label: 'Prayer Generator + Journal — Scripture-grounded prayers, saved' },
  { icon: '∞', label: 'Unlimited messages — no monthly cap' },
  { icon: '🌟', label: 'First access to every new tool I ship' },
]

export const PRICING_FAQS = [
  {
    q: 'What is the Founding Member price?',
    a: `The Founding Member price is $${FOUNDING_ANNUAL_PRICE}/year — that's just $${FOUNDING_MONTHLY_DISPLAY}/month. It's a limited-time offer for the first ${FOUNDING_SPOTS_TOTAL.toLocaleString()} subscribers who help me get off the ground. Once those spots are filled, this price goes away permanently. The regular annual price is $${PREMIUM_ANNUAL_PRICE}/year.`,
  },
  {
    q: 'How is this different from ChatGPT?',
    a: "ChatGPT answers questions. Illuminate gives you four specialized AI companions tuned for Christian conversation — Grace for everyday faith, Logos for apologetics, Spurgeon for sermon prep, and Ezra for Bible study. Plus a full Bible Reader with AI Insight in 8 translations, a Scripture-grounded Prayer Generator, and Learn Paths. These are things ChatGPT doesn't do — and I ship new tools every few weeks.",
  },
  {
    q: 'What do I get with Premium?',
    a: 'Premium unlocks all four AI companions (Grace, Logos, Spurgeon, Ezra), unlimited messages, unlimited Bible AI Insight on any passage, full conversation history, and first access to every new tool I ship — Apologetics Sparring, Sermon Prep Workflow, Scripture Memory, and more in the weeks ahead.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes, absolutely. There are no long-term commitments. You can cancel your subscription at any time from your account settings. If you cancel an annual plan, you keep access through the end of your paid period.',
  },
  {
    q: 'Is my data private?',
    a: 'Yes. I take your privacy seriously. Your conversations are stored securely and are never sold or shared with third parties. Your data is used only to personalize your Illuminate Faith experience. You can read the full Privacy Policy for details.',
  },
  {
    q: 'Do you offer a free trial?',
    a: `Two ways to start free. Every account gets ${FREE_TIER_MESSAGE_LIMIT} free messages a month with Grace — no credit card, no time limit. Or start a 7-day free trial of Premium on the monthly plan to unlock all four AI companions, unlimited messages, and every tool — you're not charged until day 7, and you can cancel anytime before then.`,
  },
]
